import React, { FC, ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

// Notion 流 KPI tile. Paper / boxShadow / border 不使用 (= 「白い紙」 原則).
// 並べる時は Grid + 余白で分離. tile 同士の縦区切りは optional left border.
interface KpiTileDelta {
  /** 表示値 (= 「+12%」 「-3秒」 等). caller が format 済の string. */
  value: string
  direction: 'up' | 'down' | 'flat'
  /** delta が positive (= 改善) かどうか. color 反転に使う. default true. */
  isPositive?: boolean
}

interface KpiTileProps {
  title: string
  value: string | number
  unit?: ReactNode
  delta?: KpiTileDelta
  /** 縦 divider (= 横並び時の区切り) を左に出す. default false. */
  divider?: boolean
}

const DELTA_ICON: Record<KpiTileDelta['direction'], ReactNode> = {
  up: <TrendingUpIcon sx={{ fontSize: 14 }} />,
  down: <TrendingDownIcon sx={{ fontSize: 14 }} />,
  flat: <TrendingFlatIcon sx={{ fontSize: 14 }} />,
}

export const KpiTile: FC<KpiTileProps> = ({
  title,
  value,
  unit,
  delta,
  divider,
}) => {
  const deltaColor = delta
    ? delta.direction === 'flat'
      ? colorTokens.fg.muted
      : (delta.isPositive ?? true) === (delta.direction === 'up')
      ? colorTokens.state.successFg
      : colorTokens.state.dangerFg
    : undefined

  return (
    <Stack
      gap={0.5}
      sx={{
        py: 1.5,
        pl: divider ? 2 : 0,
        borderLeft: divider
          ? `1px solid ${colorTokens.border.default}`
          : 'none',
        minWidth: 0,
      }}
    >
      <Typography sx={PORTAL_TEXT.kpiTitle}>{title}</Typography>
      <Stack direction="row" alignItems="baseline" gap={0.5}>
        <Typography sx={PORTAL_TEXT.kpiValue}>{value}</Typography>
        {unit && (
          <Typography component="span" sx={PORTAL_TEXT.kpiUnit}>
            {unit}
          </Typography>
        )}
      </Stack>
      {delta && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.25}
          sx={{ color: deltaColor }}
        >
          <Box sx={{ display: 'flex' }}>{DELTA_ICON[delta.direction]}</Box>
          <Typography sx={{ fontSize: 12, color: deltaColor }}>
            {delta.value}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}
